import type { Theme } from '@mui/material/styles';
import { dynamicOfferSectionsPath, offerActionPath, offerBlockPath, offerPath } from './EntityLink';

/** The offer types a stack treats specially. Everything else is a plain offer. */
export const OFFER_BLOCK = 'Offer Block';
export const DYNAMIC_OFFER_SECTION = 'Dynamic Offer Section';
/** ZET-4795: hidden wrapper offers that place a Destination or a linkout waterfall. */
export const DESTINATION = 'Destination';

export type OfferLike = {
  id: number;
  name: string;
  isActive?: boolean;
  offerType?: { id?: number; name?: string } | null;
  offerBlock?: { id?: number } | null;
  dynamicOfferSection?: { id?: number } | null;
  offerActionId?: number | null;
};

export type LibraryKind = 'offers' | 'blocks' | 'dynamic' | 'destinations';

export const LIBRARY_KINDS: { value: LibraryKind; label: string }[] = [
  { value: 'offers', label: 'Offers' },
  { value: 'blocks', label: 'Blocks' },
  { value: 'dynamic', label: 'Dynamic' },
  { value: 'destinations', label: 'Destinations' },
];

export const libraryKindOf = (offer: OfferLike): LibraryKind => {
  const type = offer.offerType?.name;
  if (type === OFFER_BLOCK) return 'blocks';
  if (type === DYNAMIC_OFFER_SECTION) return 'dynamic';
  if (type === DESTINATION) return 'destinations';
  return 'offers';
};

/** The id people know a row by: a block's or a section's own id, a destination's offer action. */
export const displayId = (offer: OfferLike) => {
  const type = offer.offerType?.name;
  if (type === OFFER_BLOCK && offer.offerBlock?.id) return { label: 'Block', id: offer.offerBlock.id };
  if (type === DYNAMIC_OFFER_SECTION && offer.dynamicOfferSection?.id)
    return { label: 'Section', id: offer.dynamicOfferSection.id };
  if (type === DESTINATION && offer.offerActionId) return { label: 'Action', id: offer.offerActionId };
  return { label: 'ID', id: offer.id };
};

/** Every id a search may match: the offer's, and the block, section or offer action it stands for. */
export const searchableIds = (offer: OfferLike) =>
  [offer.id, offer.offerBlock?.id, offer.dynamicOfferSection?.id, offer.offerActionId]
    .filter((id) => id != null)
    .map(String);

const TYPE_LABELS: Record<string, string> = { [DYNAMIC_OFFER_SECTION]: 'Dynamic' };
export const typeLabel = (offer: OfferLike) => {
  const name = offer.offerType?.name ?? '';
  return TYPE_LABELS[name] ?? name;
};

/**
 * Space-separated terms, all of which must match the name or one of the ids — the same rule the
 * classic offer list's "use spaces to separate search terms" promised.
 */
export const matchesSearch = (offer: OfferLike, query: string) => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const name = offer.name?.toLowerCase() ?? '';
  const ids = searchableIds(offer);
  return terms.every((term) => name.includes(term) || ids.some((id) => id.includes(term)));
};

/**
 * The accent on a row's leading edge. One colour per row, chosen in the same order of precedence
 * the classic editor's node legend documents: structural types first, then inactive, then
 * destination, then a layout or container override.
 */
export type Accent = 'block' | 'dynamic' | 'inactive' | 'destination' | 'override' | null;

export const accentOf = (offer: OfferLike, hasOverride = false): Accent => {
  const type = offer.offerType?.name;
  if (type === OFFER_BLOCK) return 'block';
  if (type === DYNAMIC_OFFER_SECTION) return 'dynamic';
  if (!offer.isActive) return 'inactive';
  if (type === DESTINATION) return 'destination';
  if (hasOverride) return 'override';
  return null;
};

export const ACCENTS: { value: Exclude<Accent, null>; label: string }[] = [
  { value: 'block', label: 'Offer Block' },
  { value: 'dynamic', label: 'Dynamic' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'destination', label: 'Destination' },
  { value: 'override', label: 'Layout/container override' },
];

/** The classic legend's colours, so a row reads the same in either editor. */
export const accentColor = (accent: Accent, theme: Theme) => {
  switch (accent) {
    case 'block':
      return '#800080';
    case 'dynamic':
      return '#0076b5';
    case 'inactive':
      return theme.palette.error.main;
    case 'destination':
      return theme.palette.warning.main;
    case 'override':
      return theme.palette.info.main;
    default:
      return theme.palette.divider;
  }
};

/** Where a row's offer is really configured, and how to say so: the block, the offer action, or the offer. */
export const entityLinkFor = (offer: OfferLike & { offerAction?: { id?: number } | null }) => {
  const type = offer.offerType?.name;
  if (type === OFFER_BLOCK && offer.offerBlock?.id)
    return { to: offerBlockPath(offer.offerBlock.id), label: `Open offer block ${offer.offerBlock.id} in a new tab` };
  if (type === DESTINATION) {
    const actionId = offer.offerActionId ?? offer.offerAction?.id;
    return {
      to: offerActionPath(actionId),
      label: actionId ? `Open offer action ${actionId} in a new tab` : 'Open offer actions in a new tab',
    };
  }
  if (type === DYNAMIC_OFFER_SECTION)
    return { to: dynamicOfferSectionsPath, label: 'Open dynamic offer sections in a new tab' };
  return { to: offerPath(offer.id), label: `Open offer ${offer.id} in a new tab` };
};
